import React, { FormEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  GoogleAuthProvider,
  OAuthProvider,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
  User,
} from 'firebase/auth';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faEnvelope,
  faEye,
  faEyeSlash,
  faLink,
  faLock,
  faMoon,
  faSun,
} from '@fortawesome/free-solid-svg-icons';
import { faApple, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { auth } from './Auth';
import { appContext } from '../../App';
import TOTPVerification from './TOTPVerification';
import { ensureSegPoolAccount } from './ensureSegPoolAccount';

interface TOTPCheckResponse {
  requiresTOTP: boolean;
  message?: string;
}

type ConnectMethod = 'password' | 'magic-link';

const MAGIC_LINK_EMAIL_KEY = 'segpool.connect.email';
const MAGIC_LINK_NEXT_URL_KEY = 'segpool.connect.next';
const MAGIC_LINK_REDIRECT_URL = process.env.REACT_APP_MAGIC_LINK_URL?.trim() ?? '';
const LOCAL_MAGIC_LINK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

const getSafeNextUrl = (search: string): string | null => {
  try {
    const params = new URLSearchParams(search);
    const next = params.get('next');
    if (!next || !next.trim()) {
      return null;
    }

    const target = new URL(next, window.location.origin);
    if (target.origin !== window.location.origin) {
      return null;
    }

    return target.toString();
  } catch {
    return null;
  }
};

const getStoredMagicLinkNextUrl = (): string | null => {
  const storedNext = window.localStorage.getItem(MAGIC_LINK_NEXT_URL_KEY);
  if (!storedNext) {
    return null;
  }

  try {
    const target = new URL(storedNext, window.location.origin);
    if (target.origin !== window.location.origin) {
      return null;
    }

    return target.toString();
  } catch {
    return null;
  }
};

const buildMagicLinkTargetUrl = (nextUrl?: string | null): string => {
  const targetUrl = MAGIC_LINK_REDIRECT_URL
    ? new URL(MAGIC_LINK_REDIRECT_URL, window.location.origin)
    : new URL('/connect', window.location.origin);

  if (!targetUrl.pathname || targetUrl.pathname === '/') {
    targetUrl.pathname = '/connect';
  }

  if (nextUrl) {
    targetUrl.searchParams.set('next', nextUrl);
  } else {
    targetUrl.searchParams.delete('next');
  }

  return targetUrl.toString();
};

const isLocalMagicLinkTarget = (url: string): boolean => {
  try {
    return LOCAL_MAGIC_LINK_HOSTS.has(new URL(url).hostname);
  } catch {
    return false;
  }
};

const getFirebaseErrorDetails = (error: any): string => {
  const parts = [
    error?.code,
    error?.message,
    error?.customData?._serverResponse,
    error?.customData?._tokenResponse?.error?.message,
    error?.response?.data?.error?.message,
    error?.error?.message,
  ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

  if (parts.length > 0) {
    return parts.join(' | ');
  }

  try {
    return JSON.stringify(error);
  } catch {
    return '';
  }
};

const getMagicLinkErrorMessage = (error: any, targetUrl: string): string => {
  const errorDetails = getFirebaseErrorDetails(error);

  if (error?.code === 'auth/too-many-requests' || errorDetails.includes('QUOTA_EXCEEDED') || errorDetails.includes('Exceeded daily quota for email sign-in')) {
    return 'Firebase has reached its daily quota for email-link sign-ins. Try again after the quota resets, or use password, Google, or Apple sign-in for now.';
  }

  if (error?.code === 'auth/operation-not-allowed') {
    return 'Magic link sign-in is not enabled in Firebase yet.';
  }

  if (error?.code === 'auth/invalid-continue-uri' || error?.code === 'auth/unauthorized-continue-uri') {
    const hostname = new URL(targetUrl).hostname;
    const targetLabel = isLocalMagicLinkTarget(targetUrl) ? 'localhost' : hostname;
    return `Firebase rejected the magic link redirect URL. Add ${targetLabel} to Firebase Authentication > Settings > Authorized domains${MAGIC_LINK_REDIRECT_URL ? '.' : ' or set REACT_APP_MAGIC_LINK_URL to an authorized /connect URL.'}`;
  }

  if (error?.code === 'auth/invalid-dynamic-link-domain') {
    return 'Firebase rejected the configured magic link domain. Check the email link configuration for this project.';
  }

  return 'Unable to send a magic link right now. Please try again.';
};

const ConnectScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const nextAfterLogin = useMemo(() => getSafeNextUrl(location.search), [location.search]);
  const magicLinkTargetUrl = useMemo(() => buildMagicLinkTargetUrl(nextAfterLogin), [nextAfterLogin]);
  const usesLocalMagicLinkTarget = useMemo(() => isLocalMagicLinkTarget(magicLinkTargetUrl), [magicLinkTargetUrl]);
  const initialCurrentUser = useMemo(() => auth.currentUser, []);

  const [activeMethod, setActiveMethod] = useState<ConnectMethod>('password');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMagicLinkLoading, setIsMagicLinkLoading] = useState(false);
  const [isRestoringSession, setIsRestoringSession] = useState(false);
  const [isCompletingMagicLink, setIsCompletingMagicLink] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [requiresTOTP, setRequiresTOTP] = useState(false);
  const [idToken, setIdToken] = useState('');
  const [userEmailForTOTP, setUserEmailForTOTP] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [pendingMagicEmail, setPendingMagicEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { darkMode, setDarkMode, setIsTotpVerified } = useContext(appContext);
  const isBlockingAuthFlow = isRestoringSession || isCompletingMagicLink;
  const processingTitle = isCompletingMagicLink ? 'Processing your magic link' : 'Restoring your sign-in';
  const processingMessage = isCompletingMagicLink
    ? 'Please wait while we verify your email link and connect your SegPool account.'
    : 'Please wait while we resume your existing session.';

  useEffect(() => {
    if (nextAfterLogin) {
      window.localStorage.setItem(MAGIC_LINK_NEXT_URL_KEY, nextAfterLogin);
      return;
    }

    if (!isSignInWithEmailLink(auth, window.location.href)) {
      window.localStorage.removeItem(MAGIC_LINK_NEXT_URL_KEY);
    }
  }, [nextAfterLogin]);

  const continueAfterLogin = useCallback(() => {
    const redirectTarget = nextAfterLogin || getStoredMagicLinkNextUrl();
    if (redirectTarget) {
      window.localStorage.removeItem(MAGIC_LINK_NEXT_URL_KEY);
      window.location.replace(redirectTarget);
      return;
    }

    window.localStorage.removeItem(MAGIC_LINK_NEXT_URL_KEY);
    navigate('/');
  }, [navigate, nextAfterLogin]);

  const handleSignedInUser = useCallback(async (user: User, canRequireEmailVerification: boolean) => {
    const token = await user.getIdToken();

    if (canRequireEmailVerification && !user.emailVerified) {
      await signOut(auth);
      throw new Error('email_not_verified');
    }

    await ensureSegPoolAccount(user, token);

    const totpCheckResponse = await axios.get<TOTPCheckResponse>('/pool/api/totp/check', {
      params: { email: user.email },
      headers: { Authorization: `Bearer ${token}` },
    });

    if (totpCheckResponse.data.requiresTOTP) {
      setUserEmailForTOTP(user.email || email || pendingMagicEmail);
      setIdToken(token);
      setRequiresTOTP(true);
      return;
    }

    continueAfterLogin();
  }, [continueAfterLogin, email, pendingMagicEmail]);

  const handlePasswordSignIn = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await handleSignedInUser(userCredential.user, true);
    } catch (error: any) {
      if (error?.message === 'email_not_verified') {
        setErrorMessage('Your email is not verified yet. Please use the verification email from your account setup.');
      } else if (error?.message === 'no_segpool_account') {
        setErrorMessage('No SegPool account is linked to this identity yet. Use an existing linked account to continue.');
      } else {
        setErrorMessage(
          error?.code === 'auth/wrong-password' || error?.code === 'auth/user-not-found' || error?.code === 'auth/invalid-credential'
            ? 'Invalid email or password'
            : 'Unable to sign you in right now. Please try again.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleProviderSignIn = async (providerName: 'google' | 'apple') => {
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const provider = providerName === 'google'
        ? new GoogleAuthProvider()
        : new OAuthProvider('apple.com');

      const credential = await signInWithPopup(auth, provider);
      await handleSignedInUser(credential.user, false);
    } catch (error: any) {
      if (error?.message === 'no_segpool_account') {
        setErrorMessage('This sign-in method is not linked to a SegPool account yet. Use a linked identity to continue.');
      } else if (error?.code === 'auth/popup-closed-by-user') {
        setErrorMessage('The sign-in window was closed before the request completed.');
      } else if (error?.code === 'auth/operation-not-allowed') {
        setErrorMessage(`${providerName === 'google' ? 'Google' : 'Apple'} sign-in is not enabled in Firebase yet.`);
      } else {
        setErrorMessage(`Unable to sign in with ${providerName === 'google' ? 'Google' : 'Apple'} right now.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMagicLink = async (event: FormEvent) => {
    event.preventDefault();
    setIsMagicLinkLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      if (nextAfterLogin) {
        window.localStorage.setItem(MAGIC_LINK_NEXT_URL_KEY, nextAfterLogin);
      } else {
        window.localStorage.removeItem(MAGIC_LINK_NEXT_URL_KEY);
      }

      await sendSignInLinkToEmail(auth, pendingMagicEmail, {
        url: magicLinkTargetUrl,
        handleCodeInApp: true,
      });

      window.localStorage.setItem(MAGIC_LINK_EMAIL_KEY, pendingMagicEmail);
      setSuccessMessage(`Magic link sent to ${pendingMagicEmail}. Open it on this device to continue.`);
    } catch (error: any) {
      console.error('Unable to send magic link', { error, magicLinkTargetUrl });
      setErrorMessage(getMagicLinkErrorMessage(error, magicLinkTargetUrl));
    } finally {
      setIsMagicLinkLoading(false);
    }
  };

  const handleResetPassword = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setSuccessMessage(`Password reset email sent to ${resetEmail}.`);
      setShowResetForm(false);
      setResetEmail('');
    } catch (error: any) {
      if (error?.code === 'auth/user-not-found') {
        setErrorMessage('No user was found for that email address.');
      } else {
        setErrorMessage('Unable to send the reset email right now.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!initialCurrentUser) {
      return;
    }

    let cancelled = false;

    const resumeAuthenticatedFlow = async () => {
      setIsRestoringSession(true);
      setIsLoading(true);
      setErrorMessage(null);
      setSuccessMessage(null);

      try {
        await handleSignedInUser(initialCurrentUser, false);
      } catch (error: any) {
        if (!cancelled) {
          if (error?.message === 'no_segpool_account') {
            setErrorMessage('We could not link your SegPool account automatically. Please try again.');
          } else {
            setErrorMessage('Unable to continue your SegPool sign-in right now. Please try again.');
          }
        }
      } finally {
        if (!cancelled) {
          setIsRestoringSession(false);
          setIsLoading(false);
        }
      }
    };

    resumeAuthenticatedFlow();

    return () => {
      cancelled = true;
    };
  }, [handleSignedInUser, initialCurrentUser]);

  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      return;
    }

    const storedEmail = window.localStorage.getItem(MAGIC_LINK_EMAIL_KEY) || email || pendingMagicEmail;
    if (!storedEmail) {
      setActiveMethod('magic-link');
      setErrorMessage('Enter the same email address you used for the magic link to finish signing in.');
      return;
    }

    let cancelled = false;

    const completeMagicLink = async () => {
      setIsCompletingMagicLink(true);
      setIsMagicLinkLoading(true);
      setErrorMessage(null);
      setSuccessMessage(null);

      try {
        const credential = await signInWithEmailLink(auth, storedEmail, window.location.href);
        window.localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);

        if (!cancelled) {
          await handleSignedInUser(credential.user, false);
        }
      } catch (error: any) {
        if (!cancelled) {
          if (error?.message === 'no_segpool_account') {
            setErrorMessage('No SegPool account is linked to this email yet. Use an existing linked account to continue.');
          } else {
            setErrorMessage('Unable to complete magic link sign-in. Request a new link and try again.');
          }
        }
      } finally {
        if (!cancelled) {
          setIsCompletingMagicLink(false);
          setIsMagicLinkLoading(false);
        }
      }
    };

    completeMagicLink();

    return () => {
      cancelled = true;
    };
  }, [email, handleSignedInUser, pendingMagicEmail]);

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white dark:bg-slate-950 transition-colors duration-500 overflow-hidden">
      <div className="hidden md:flex md:w-1/2 lg:w-3/5 bg-[#0c2a4a] relative overflow-hidden items-center justify-center p-12">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-cyan-400/10 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-400/10 rounded-full blur-[150px] animate-pulse"></div>

        <div className="relative z-10 max-w-lg text-center">
          <img src="/assets/segpool.png" alt="SegPool Logo" className="h-20 mx-auto mb-4 drop-shadow-2xl brightness-110" />
          <h1 className="text-4xl lg:text-5xl font-black text-white mb-6 tracking-tight">
            Connect Your <span className="text-cyan-300">SegPool</span> Account
          </h1>
          <p className="text-blue-100/70 text-lg lg:text-xl font-medium leading-relaxed">
            Sign in with an existing account to authorize SegPool access for connected applications.
          </p>
        </div>
      </div>

      <div className="flex-1 flex flex-col relative bg-white dark:bg-slate-950/40 overflow-y-auto">
        <div className="p-4 md:p-6 flex items-center z-50">
          <div className="flex-1 flex justify-end gap-2">
            <Link
              to="/"
              className="p-3 bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400 rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-800 transition-all border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-center"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
            </Link>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-3 bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400 rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-800 transition-all border border-slate-200 dark:border-slate-800 shadow-sm"
            >
              <FontAwesomeIcon icon={darkMode ? faSun : faMoon} className="text-lg" />
            </button>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 md:p-12 lg:p-20">
          {requiresTOTP ? (
            <div className="w-full max-w-md animate-in zoom-in-95 duration-500">
              <TOTPVerification
                email={userEmailForTOTP}
                idToken={idToken}
                onVerify={() => {
                  setIsTotpVerified(true);
                  continueAfterLogin();
                }}
                onCancel={() => {
                  setRequiresTOTP(false);
                  auth.signOut();
                }}
              />
            </div>
          ) : isBlockingAuthFlow ? (
            <div className="w-full max-w-md animate-in fade-in zoom-in-95 duration-500">
              <div className="rounded-[2rem] border border-slate-200 bg-white/95 p-8 shadow-2xl shadow-slate-200/70 dark:border-slate-800 dark:bg-slate-900/80 dark:shadow-black/30 md:p-10">
                <div className="flex flex-col items-center text-center" role="status" aria-live="polite">
                  <img
                    src="/assets/segmentslogo.png"
                    alt="SegPool Logo"
                    className="mb-6 h-14 drop-shadow-2xl brightness-110"
                  />
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                    <div className="h-7 w-7 rounded-full border-[3px] border-current/20 border-t-current animate-spin" />
                  </div>
                  <h3 className="text-2xl font-black tracking-tight text-[#0c2a4a] dark:text-white">
                    {processingTitle}
                  </h3>
                  <p className="mt-3 max-w-sm text-sm font-medium leading-relaxed text-slate-500 dark:text-slate-400">
                    {processingMessage}
                  </p>
                  <div className="mt-6 h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                    <div className="auth-progress-bar h-full w-1/3 rounded-full bg-gradient-to-r from-cyan-400 via-blue-500 to-[#0A3A6B]" />
                  </div>
                  <p className="mt-4 text-[11px] font-black uppercase tracking-[0.28em] text-slate-400 dark:text-slate-500">
                    Please keep this tab open
                  </p>
                </div>
              </div>
              <style>{`
                @keyframes auth-progress-slide {
                  0% { transform: translateX(-130%); }
                  100% { transform: translateX(320%); }
                }

                .auth-progress-bar {
                  animation: auth-progress-slide 1.4s infinite ease-in-out;
                }
              `}</style>
            </div>
          ) : (
            <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-8 duration-700">
              <div className="mb-6 md:mb-8 text-center md:text-left">
                <img
                  src="/assets/segmentslogo.png"
                  alt="SegPool Logo"
                  className="h-14 mx-auto mb-4 drop-shadow-2xl brightness-110 md:hidden"
                />
                <h2 className="text-3xl md:text-4xl font-black text-[#0c2a4a] dark:text-white mb-2 tracking-tight">Sign In to Continue</h2>
                <p className="text-slate-500 dark:text-slate-400 font-medium text-[10px] md:text-sm uppercase tracking-widest opacity-80">
                  Sign-up is disabled on this page. Use an existing linked account.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => handleProviderSignIn('google')}
                  disabled={isLoading || isMagicLinkLoading}
                  className="w-full py-3.5 px-4 bg-white dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 hover:border-blue-400 dark:hover:border-blue-500 transition-all font-bold text-sm md:text-base flex items-center justify-center gap-3 disabled:opacity-60"
                >
                  <FontAwesomeIcon icon={faGoogle} className="text-base text-[#EA4335]" />
                  <span>Google</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleProviderSignIn('apple')}
                  disabled={isLoading || isMagicLinkLoading}
                  className="w-full py-3.5 px-4 bg-white dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 hover:border-blue-400 dark:hover:border-blue-500 transition-all font-bold text-sm md:text-base flex items-center justify-center gap-3 disabled:opacity-60"
                >
                  <FontAwesomeIcon icon={faApple} className="text-lg" />
                  <span>Apple</span>
                </button>
              </div>

              <div className="my-6 flex items-center gap-4">
                <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800"></div>
                <span className="text-[10px] md:text-xs font-black uppercase tracking-[0.3em] text-slate-400">or</span>
                <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800"></div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6">
                <button
                  type="button"
                  onClick={() => {
                    setActiveMethod('password');
                    setShowResetForm(false);
                  }}
                  className={`rounded-2xl border px-4 py-3 text-xs md:text-sm font-black uppercase tracking-widest transition-all ${
                    activeMethod === 'password'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-500/10 dark:text-blue-300'
                      : 'border-slate-200 text-slate-500 hover:border-slate-300 dark:border-slate-800 dark:text-slate-400'
                  }`}
                >
                  Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveMethod('magic-link');
                    setShowResetForm(false);
                  }}
                  className={`rounded-2xl border px-4 py-3 text-xs md:text-sm font-black uppercase tracking-widest transition-all ${
                    activeMethod === 'magic-link'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-500/10 dark:text-blue-300'
                      : 'border-slate-200 text-slate-500 hover:border-slate-300 dark:border-slate-800 dark:text-slate-400'
                  }`}
                >
                  Magic Link
                </button>
              </div>

              {!showResetForm && activeMethod === 'password' && (
                <form onSubmit={handlePasswordSignIn} className="space-y-4 md:space-y-6">
                  <div className="space-y-1 md:space-y-2">
                    <label className="text-[10px] md:text-xs font-black text-slate-500 dark:text-slate-400 ml-1 uppercase tracking-widest" htmlFor="connect-email">
                      Email Address
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                        <FontAwesomeIcon icon={faEnvelope} className="text-xs md:text-sm" />
                      </div>
                      <input
                        className="w-full pl-11 pr-4 py-3 md:py-4 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-medium text-sm md:text-base placeholder:text-slate-400"
                        type="email"
                        id="connect-email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        required
                        placeholder="name@example.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-1 md:space-y-2">
                    <div className="flex justify-between items-center px-1">
                      <label className="text-[10px] md:text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest" htmlFor="connect-password">
                        Password
                      </label>
                      <button
                        type="button"
                        className="text-[10px] md:text-xs font-black text-blue-600 dark:text-blue-400 hover:underline tracking-widest"
                        onClick={() => setShowResetForm(true)}
                      >
                        FORGOT PASSWORD?
                      </button>
                    </div>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                        <FontAwesomeIcon icon={faLock} className="text-xs md:text-sm" />
                      </div>
                      <input
                        className="w-full pl-11 pr-12 py-3 md:py-4 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-medium text-sm md:text-base placeholder:text-slate-400"
                        type={showPassword ? 'text' : 'password'}
                        id="connect-password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        required
                        placeholder="********"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="text-xs md:text-sm" />
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || isMagicLinkLoading}
                    className="w-full py-3.5 md:py-4 bg-[#0A3A6B] hover:bg-[#0c4a8a] text-white rounded-2xl font-black text-base md:text-lg shadow-xl shadow-blue-500/10 hover:shadow-blue-500/20 active:scale-[0.98] transition-all disabled:opacity-70 disabled:scale-100 flex items-center justify-center gap-3 h-[54px] md:h-[60px]"
                  >
                    {isLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span className="text-sm md:text-base tracking-widest uppercase">SIGNING IN...</span>
                      </div>
                    ) : (
                      <>
                        <span>SIGN IN</span>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs opacity-70" />
                      </>
                    )}
                  </button>
                </form>
              )}

              {!showResetForm && activeMethod === 'magic-link' && (
                <form onSubmit={handleSendMagicLink} className="space-y-4 md:space-y-6">
                   <div className="space-y-1 md:space-y-2">
                    <label className="text-[10px] md:text-xs font-black text-slate-500 dark:text-slate-400 ml-1 uppercase tracking-widest" htmlFor="magic-link-email">
                      Email Address
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                        <FontAwesomeIcon icon={faLink} className="text-xs md:text-sm" />
                      </div>
                      <input
                        className="w-full pl-11 pr-4 py-3 md:py-4 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-medium text-sm md:text-base placeholder:text-slate-400"
                        type="email"
                        id="magic-link-email"
                        value={pendingMagicEmail}
                        onChange={(event) => setPendingMagicEmail(event.target.value)}
                        required
                        placeholder="name@example.com"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || isMagicLinkLoading}
                    className="w-full py-3.5 md:py-4 bg-[#0A3A6B] hover:bg-[#0c4a8a] text-white rounded-2xl font-black text-base md:text-lg shadow-xl shadow-blue-500/10 hover:shadow-blue-500/20 active:scale-[0.98] transition-all disabled:opacity-70 disabled:scale-100 flex items-center justify-center gap-3 h-[54px] md:h-[60px]"
                  >
                    {isMagicLinkLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span className="text-sm md:text-base tracking-widest uppercase">SENDING LINK...</span>
                      </div>
                    ) : (
                      <>
                        <span>SEND MAGIC LINK</span>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs opacity-70" />
                      </>
                    )}
                  </button>
                </form>
              )}

              {showResetForm && (
                <form onSubmit={handleResetPassword} className="space-y-4 md:space-y-6">
                  <div className="mb-1 text-center md:text-left">
                    <h3 className="text-2xl font-black text-[#0c2a4a] dark:text-white">Reset Password</h3>
                    <p className="text-slate-500 dark:text-slate-400 font-medium text-xs md:text-sm mt-1">
                      Enter the email for your existing SegPool account.
                    </p>
                  </div>

                  <div className="space-y-1 md:space-y-2">
                    <label className="text-[10px] md:text-xs font-black text-slate-500 dark:text-slate-400 ml-1 uppercase tracking-widest" htmlFor="reset-email">
                      Email Address
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                        <FontAwesomeIcon icon={faEnvelope} className="text-xs md:text-sm" />
                      </div>
                      <input
                        className="w-full pl-11 pr-4 py-3 md:py-4 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-white rounded-2xl border border-slate-200 dark:border-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-medium text-sm md:text-base placeholder:text-slate-400"
                        type="email"
                        id="reset-email"
                        value={resetEmail}
                        onChange={(event) => setResetEmail(event.target.value)}
                        required
                        placeholder="name@example.com"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-3.5 md:py-4 bg-[#0A3A6B] text-white rounded-2xl font-black text-base md:text-lg hover:bg-[#0c4a8a] transition-all flex items-center justify-center gap-3 h-[54px] md:h-[60px] disabled:opacity-70"
                    >
                      {isLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span className="text-sm md:text-base tracking-widest uppercase">SENDING...</span>
                        </div>
                      ) : 'SEND RESET EMAIL'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowResetForm(false)}
                      className="w-full py-3 text-slate-500 dark:text-slate-400 font-bold hover:bg-slate-50 dark:hover:bg-slate-900/50 rounded-2xl transition-all text-xs md:text-sm font-black"
                    >
                      BACK TO SIGN IN
                    </button>
                  </div>
                </form>
              )}

              {(errorMessage || successMessage) && (
                <div className="mt-6 animate-in slide-in-from-bottom-6 duration-300">
                  <div
                    className={`p-4 rounded-2xl shadow-lg flex items-center justify-between gap-3 ${
                      successMessage
                        ? 'bg-green-500 text-white'
                        : 'bg-red-500 text-white'
                    }`}
                  >
                    <p className="font-bold flex-1 text-xs md:text-sm">{successMessage || errorMessage}</p>
                    <button
                      type="button"
                      className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                      onClick={() => {
                        setErrorMessage(null);
                        setSuccessMessage(null);
                      }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectScreen;
